import React, { useEffect, useState } from 'react';
import { auth } from '../config/firebase';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';

export const DebugAuthPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    console.log(message);
    setLogs((prev) => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  useEffect(() => {
    addLog('DebugAuthPage mounted');
    addLog(`Current user from auth: ${auth.currentUser?.email || 'null'}`);

    // Listen to auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      addLog(`onAuthStateChanged fired: user = ${user?.email || 'null'}`);
      setCurrentUser(user);
    });

    return unsubscribe;
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      addLog(`Attempting login with ${email}`);

      const result = await signInWithEmailAndPassword(auth, email, password);
      addLog(`Login successful: ${result.user.email}`);
      addLog(`Auth.currentUser is now: ${auth.currentUser?.email}`);
      addLog(`State will update via onAuthStateChanged listener`);
    } catch (error: any) {
      addLog(`Login failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h1>🐛 Firebase Auth Debug Page</h1>

      <div style={{ background: '#f0f0f0', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
        <h3>Current Auth State:</h3>
        <pre style={{ background: 'white', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
          {currentUser ? (
            <>
              Email: {currentUser.email}
              <br />
              UID: {currentUser.uid}
              <br />
              Email Verified: {currentUser.emailVerified}
            </>
          ) : (
            'Not logged in'
          )}
        </pre>
      </div>

      {!currentUser ? (
        <form onSubmit={handleLogin} style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '10px' }}>
            <label>Email: </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%', padding: '8px', marginTop: '5px' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label>Password: </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: '100%', padding: '8px', marginTop: '5px' }}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: 'var(--primary-button)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Logging in...' : 'Test Login'}
          </button>
        </form>
      ) : (
        <button
          onClick={() => {
            addLog('User logged in - refresh page to test dashboard redirect');
          }}
          style={{
            padding: '10px 20px',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          ✅ Logged in! Refresh page to test redirect
        </button>
      )}

      <h3>Console Logs:</h3>
      <div
        style={{
          background: '#1e1e1e',
          color: '#00ff00',
          padding: '15px',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '12px',
          maxHeight: '400px',
          overflow: 'auto',
        }}
      >
        {logs.map((log, i) => (
          <div key={i}>{log}</div>
        ))}
      </div>

      <h3 style={{ marginTop: '20px' }}>🔍 What This Tests:</h3>
      <ul>
        <li>Firebase Auth connection</li>
        <li>Login credentials validation</li>
        <li>Auth state listener firing</li>
        <li>Session persistence</li>
        <li>Cloudflare domain compatibility</li>
      </ul>

      <div style={{ marginTop: '20px', padding: '10px', background: '#fef3c7', borderRadius: '4px' }}>
        <strong>Instructions:</strong>
        <ol>
          <li>Enter your test email and password</li>
          <li>Click "Test Login"</li>
          <li>Watch the console logs below</li>
          <li>If login succeeds, refresh the page</li>
          <li>You should stay logged in (session persists)</li>
        </ol>
      </div>

      <div style={{ marginTop: '20px', padding: '10px', background: '#fee2e2', borderRadius: '4px' }}>
        <strong>⚠️ Important:</strong> This is a debug page. It directly tests Firebase Auth without our custom logic.
        If this works but the real login doesn't, the issue is in our code. If this fails, the issue is Firebase/Cloudflare.
      </div>
    </div>
  );
};
